import React, { createContext, useContext, useState } from "react";

const defaultTheme = {
	colors: {
		// Primary colors
		primary: "#007AFF", // iOS blue
		secondary: "#5856D6", // iOS purple
		success: "#34C759", // iOS green
		warning: "#FF9500", // iOS orange
		error: "#FF3B30", // iOS red
		info: "#5AC8FA", // iOS light blue

		// Background colors
		background: "#F2F2F7", // iOS light gray background
		card: "#FFFFFF",
		modal: "#FFFFFF",

		// Text colors
		text: "#000000",
		textSecondary: "#8E8E93", // iOS gray
		textTertiary: "#C7C7CC", // iOS lighter gray

		// Border and separator colors
		border: "#C6C6C8", // iOS light border
		separator: "#E5E5EA", // iOS separator

		// System grays
		gray: {
			1: "#8E8E93",
			2: "#AEAEB2",
			3: "#C7C7CC",
			4: "#D1D1D6",
			5: "#E5E5EA",
			6: "#F2F2F7",
		},

		// System backgrounds
		systemBackground: {
			primary: "#FFFFFF",
			secondary: "#F2F2F7",
			tertiary: "#FFFFFF",
		},

		// Grouped backgrounds
		groupedBackground: {
			primary: "#F2F2F7",
			secondary: "#FFFFFF",
			tertiary: "#F2F2F7",
		},
	},

	spacing: {
		xs: 4,
		sm: 8,
		md: 16,
		lg: 24,
		xl: 32,
		xxl: 40,
	},

	borderRadius: {
		xs: 4,
		sm: 8,
		md: 12,
		lg: 16,
		xl: 20,
		pill: 9999,
	},

	typography: {
		largeTitle: {
			fontSize: 34,
			lineHeight: 41,
			fontWeight: "bold",
		},
		title1: {
			fontSize: 28,
			lineHeight: 34,
			fontWeight: "bold",
		},
		title2: {
			fontSize: 22,
			lineHeight: 28,
			fontWeight: "bold",
		},
		title3: {
			fontSize: 20,
			lineHeight: 25,
			fontWeight: "600",
		},
		headline: {
			fontSize: 17,
			lineHeight: 22,
			fontWeight: "600",
		},
		body: {
			fontSize: 17,
			lineHeight: 22,
			fontWeight: "normal",
		},
		callout: {
			fontSize: 16,
			lineHeight: 21,
			fontWeight: "normal",
		},
		subhead: {
			fontSize: 15,
			lineHeight: 20,
			fontWeight: "normal",
		},
		footnote: {
			fontSize: 13,
			lineHeight: 18,
			fontWeight: "normal",
		},
		caption1: {
			fontSize: 12,
			lineHeight: 16,
			fontWeight: "normal",
		},
		caption2: {
			fontSize: 11,
			lineHeight: 13,
			fontWeight: "normal",
		},
	},

	shadows: {
		sm: {
			shadowColor: "#000",
			shadowOffset: { width: 0, height: 1 },
			shadowOpacity: 0.18,
			shadowRadius: 1.0,
			elevation: 1,
		},
		md: {
			shadowColor: "#000",
			shadowOffset: { width: 0, height: 2 },
			shadowOpacity: 0.2,
			shadowRadius: 1.41,
			elevation: 2,
		},
		lg: {
			shadowColor: "#000",
			shadowOffset: { width: 0, height: 3 },
			shadowOpacity: 0.22,
			shadowRadius: 2.22,
			elevation: 3,
		},
	},
};

const ThemeContext = createContext({
	theme: defaultTheme,
	setTheme: () => {},
});

export const ThemeProvider = ({ children }) => {
	const [theme, setTheme] = useState(defaultTheme);

	return <ThemeContext.Provider value={{ theme, setTheme }}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
	const context = useContext(ThemeContext);
	if (context === undefined) {
		throw new Error("useTheme must be used within a ThemeProvider");
	}
	return context.theme;
};

export default ThemeContext;
